import {ErrorType} from '~~/package/mapper/learning/model/CodeRunModel'

// Lifecycle of a single coderun "Run" click, reduced to ONE `code_run_finished` Faro event.
//
// Pure (no Nuxt / Faro / DOM dependency): the emitter, clock and timers are injected so
// the outcome rules are unit-testable. Only opaque ids and low-cardinality dimensions are
// carried — never code, program output or error text.

export type CodeRunOutcome =
    | 'ok'            // an end frame arrived with no error
    | 'code_error'    // an end/error frame with errorType CODE_ERROR
    | 'server_error'  // a SERVER_ERROR frame or a socket close before a terminal frame
    | 'connect_fail'  // CONNECT_FAIL dialog after repeated WebSocket errors
    | 'no_end'        // no end frame within CODE_RUN_NO_END_TIMEOUT_MS of the click
    | 'superseded'    // a new Run started while this one was in flight (excluded from rates)
    | 'user_stopped'  // the Stop button closed the socket mid-run (excluded from rates)

export interface CodeRunMeta {
    runId: string
    langType?: string
    taskType?: string
    domain?: string
    domainId?: number
    taskId?: number
}

/** The subset of a coderun WebSocket frame the tracker reads. */
export interface CodeRunFrame {
    end?: boolean
    errorType?: ErrorType | null
}

export interface CodeRunFinished extends CodeRunMeta {
    outcome: CodeRunOutcome
    firstOutputMs?: number
    totalMs: number
    wsRetryCount: number
}

/** The proxy cuts a run at 10 minutes; allow one extra minute for the final frame. */
export const CODE_RUN_NO_END_TIMEOUT_MS = 11 * 60 * 1000

export interface CodeRunTracker {
    /** A Run was clicked. Any run still in flight is finalized as `superseded`. */
    start(meta: CodeRunMeta): void
    /** runId of the in-flight run, for the WebSocket message. */
    currentRunId(): string | undefined
    onFrame(frame: CodeRunFrame): void
    /** A WebSocket error that schedules a reconnect. */
    onWsRetry(): void
    onConnectFail(): void
    onUserStop(): void
    /** The WebSocket closed before a terminal frame. */
    onSocketClose(): void

}

interface ActiveRun {
    meta: CodeRunMeta
    startedAt: number
    firstFrameAt?: number
    sawCodeError: boolean
    wsRetryCount: number
    timer?: ReturnType<typeof setTimeout>
}

export const createCodeRunTracker = (emit: (finished: CodeRunFinished) => void): CodeRunTracker => {
    let active: ActiveRun | null = null

    const finish = (outcome: CodeRunOutcome) => {
        const run = active
        if (!run) return
        active = null
        clearTimeout(run.timer)
        try {
            emit({
                ...run.meta,
                outcome,
                firstOutputMs: run.firstFrameAt === undefined ? undefined : run.firstFrameAt - run.startedAt,
                totalMs: Date.now() - run.startedAt,
                wsRetryCount: run.wsRetryCount,
            })
        } catch {
            // Observability must never break the run flow.
        }
    }

    return {
        start(meta) {
            finish('superseded')
            const run: ActiveRun = { meta, startedAt: Date.now(), sawCodeError: false, wsRetryCount: 0 }
            active = run
            run.timer = setTimeout(() => {
                if (active === run) finish('no_end')
            }, CODE_RUN_NO_END_TIMEOUT_MS)
        },

        currentRunId: () => active?.meta.runId,

        onFrame(frame) {
            const run = active
            if (!run) return
            if (run.firstFrameAt === undefined) run.firstFrameAt = Date.now()

            if (frame.errorType === ErrorType.SERVER_ERROR) {
                finish('server_error')
            } else if (frame.errorType === ErrorType.CODE_ERROR) {
                // SQL runners send a non-final CODE_ERROR frame followed by a plain end frame.
                run.sawCodeError = true
                if (frame.end) finish('code_error')
            } else if (frame.end) {
                finish(run.sawCodeError ? 'code_error' : 'ok')
            }
        },

        onWsRetry() {
            if (active) active.wsRetryCount += 1
        },

        onConnectFail: () => finish('connect_fail'),
        onUserStop: () => finish('user_stopped'),
        onSocketClose: () => finish('server_error'),
    }
}

/** UUID v4; crypto.randomUUID needs a secure context, so fall back to getRandomValues / Math.random. */
export const generateRunId = (): string => {
    const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined
    if (c?.randomUUID) {
        try {
            return c.randomUUID()
        } catch {
            // fall through
        }
    }
    const bytes = new Uint8Array(16)
    if (c?.getRandomValues) c.getRandomValues(bytes)
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
